import type { Game, PlayerWeekScore, WeekSheet } from './types';
import type { PlayerId } from './roster';
import { PLAYER_IDS } from './roster';

export function gameWinner(game: Game): string | null {
  if (game.status !== 'final') return null;
  const away = game.awayScore;
  const home = game.homeScore;
  if (away === undefined || home === undefined) return null;
  if (away === home) return null;
  return away > home ? game.away.id : game.home.id;
}

export function scoreSheet(sheet: WeekSheet, games: Game[]): number {
  let points = 0;
  for (const game of games) {
    const winner = gameWinner(game);
    if (!winner) continue;
    const pick = sheet.picks[game.id];
    if (pick && pick === winner) points += 1;
  }
  return points;
}

export function scoreWeek(
  sheets: Record<PlayerId, WeekSheet>,
  games: Game[],
): PlayerWeekScore[] {
  const tiebreakerGame = findTiebreakerGame(games);
  const actualTotal = tiebreakerGame ? finalTotalPoints(tiebreakerGame) : null;

  return PLAYER_IDS.map((playerId) => {
    const sheet = sheets[playerId] ?? {
      playerId,
      weekKey: '',
      picks: {},
    };
    const points = scoreSheet(sheet, games);
    const tiebreaker = sheet.tiebreaker;
    let tiebreakerDelta: number | null = null;
    if (actualTotal !== null && tiebreaker !== undefined) {
      tiebreakerDelta = Math.abs(tiebreaker - actualTotal);
    }
    return { playerId, points, tiebreaker, tiebreakerDelta };
  });
}

function weekdayInNewYork(iso: string): string {
  return new Intl.DateTimeFormat('en-US', {
    weekday: 'short',
    timeZone: 'America/New_York',
  }).format(new Date(iso));
}

/** Monday Night Football in ET, else the week's last kickoff. */
export function findTiebreakerGame(games: Game[]): Game | null {
  if (games.length === 0) return null;
  const sorted = [...games].sort(
    (a, b) => Date.parse(a.kickoffUtc) - Date.parse(b.kickoffUtc),
  );
  const mondayEt = sorted.filter((g) => weekdayInNewYork(g.kickoffUtc) === 'Mon');
  if (mondayEt.length > 0) {
    return mondayEt[mondayEt.length - 1];
  }
  return sorted[sorted.length - 1];
}

export function finalTotalPoints(game: Game): number | null {
  if (game.status !== 'final') return null;
  const away = game.awayScore;
  const home = game.homeScore;
  if (away === undefined || home === undefined) return null;
  return away + home;
}

/** How many games this sheet has a team picked for. */
export function pickProgress(
  sheet: WeekSheet | undefined,
  games: Game[],
): { made: number; total: number } {
  const picks = sheet?.picks ?? {};
  const ids = new Set(games.map((g) => g.id));
  let made = 0;
  for (const [gameId, teamId] of Object.entries(picks)) {
    if (teamId && ids.has(gameId)) made += 1;
  }
  return { made, total: games.length };
}

export function picksForTeam(
  gameId: string,
  sheets: Record<PlayerId, WeekSheet>,
): Map<string, PlayerId[]> {
  const byTeam = new Map<string, PlayerId[]>();
  for (const playerId of PLAYER_IDS) {
    const teamId = sheets[playerId]?.picks[gameId];
    if (!teamId) continue;
    const list = byTeam.get(teamId) ?? [];
    list.push(playerId);
    byTeam.set(teamId, list);
  }
  return byTeam;
}

export function scoreSeason(
  weeks: { games: Game[]; sheets: Record<PlayerId, WeekSheet> }[],
  adjustments: Partial<Record<PlayerId, number>> = {},
): { playerId: PlayerId; seasonPoints: number; weeksPlayed: number }[] {
  return PLAYER_IDS.map((playerId) => {
    let seasonPoints = adjustments[playerId] ?? 0;
    let weeksPlayed = 0;
    for (const week of weeks) {
      const pts = scoreSheet(
        week.sheets[playerId] ?? { playerId, weekKey: '', picks: {} },
        week.games,
      );
      seasonPoints += pts;
      weeksPlayed += 1;
    }
    return { playerId, seasonPoints, weeksPlayed };
  });
}
